/**
 * Firebase Web SDK — single source of truth.
 *
 * Reads config from /api/firebase/config at runtime so the user can paste their
 * Firebase Web Config into backend/.env without rebuilding the frontend. Until
 * the config arrives, every helper here is a graceful no-op.
 *
 * Set up: see /app/memory/FIREBASE_SETUP.md
 */

import { initializeApp, getApps } from "firebase/app";
import { getAuth, signInWithCustomToken, onAuthStateChanged, signOut as fbSignOut } from "firebase/auth";
import { getFirestore, doc, setDoc, getDoc, onSnapshot } from "firebase/firestore";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { getAnalytics, isSupported as analyticsSupported, logEvent } from "firebase/analytics";
import { getMessaging, getToken, onMessage, isSupported as messagingSupported } from "firebase/messaging";
import api from "./api";

let state = {
  app: null, auth: null, db: null, storage: null, analytics: null, messaging: null,
  config: null, vapidKey: null, ready: false,
};

let initPromise = null;

export async function initFirebase() {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    try {
      const { data } = await api.get("/firebase/config");
      const cfg = data?.config || {};
      // Need at least apiKey + authDomain + projectId + appId
      if (!cfg.apiKey || !cfg.authDomain || !cfg.projectId || !cfg.appId) {
        return state; // graceful no-op
      }
      if (getApps().length === 0) {
        state.app = initializeApp(cfg);
      } else {
        state.app = getApps()[0];
      }
      state.config = cfg;
      state.vapidKey = data.vapid_key || null;
      state.auth = getAuth(state.app);
      state.db = getFirestore(state.app);
      state.storage = getStorage(state.app);
      if (cfg.measurementId && (await analyticsSupported())) {
        state.analytics = getAnalytics(state.app);
      }
      if (await messagingSupported()) {
        state.messaging = getMessaging(state.app);
      }
      state.ready = true;
    } catch (e) {
      console.warn("[Firebase] init failed:", e?.message);
    }
    return state;
  })();
  return initPromise;
}

export function firebase() { return state; }
export const isFirebaseReady = () => state.ready;

// --- Firestore helpers -------------------------------------------------------
export async function mirrorUserProfile(user) {
  if (!state.ready || !user?.id) return;
  try {
    await setDoc(doc(state.db, "users", user.id), {
      id: user.id,
      username: user.username,
      display_name: user.display_name,
      avatar_path: user.avatar_path,
      country: user.country,
      is_verified: !!user.is_verified,
      is_vip: !!user.is_vip,
      wave_balance: user.wave_balance ?? 0,
      pearl_balance: user.pearl_balance ?? 0,
      total_pearls_earned: user.total_pearls_earned ?? 0,
      synced_at: new Date().toISOString(),
    }, { merge: true });
  } catch (e) { console.warn("[Firebase] mirrorUserProfile", e?.message); }
}

export function watchUserDoc(uid, cb) {
  if (!state.ready || !uid) return () => {};
  return onSnapshot(doc(state.db, "users", uid), (snap) => cb(snap.data()));
}

// --- Storage helpers ---------------------------------------------------------
export async function uploadToFirebase(file, pathPrefix = "uploads") {
  if (!state.ready) throw new Error("Firebase not configured");
  const safeName = `${pathPrefix}/${Date.now()}-${file.name}`;
  const r = storageRef(state.storage, safeName);
  await uploadBytes(r, file);
  const url = await getDownloadURL(r);
  return { path: safeName, url };
}

// --- Analytics ---------------------------------------------------------------
export function track(event, params = {}) {
  if (state.analytics) {
    try { logEvent(state.analytics, event, params); } catch (e) { /* ignore */ }
  }
}

// --- FCM Push (alternative to OneSignal — works inside Capacitor or web) ----
export async function registerForPush() {
  if (!state.ready || !state.messaging || !state.vapidKey) return null;
  try {
    const token = await getToken(state.messaging, { vapidKey: state.vapidKey });
    return token;
  } catch (e) { return null; }
}

export function onForegroundPush(cb) {
  if (!state.ready || !state.messaging) return () => {};
  return onMessage(state.messaging, cb);
}

// --- Auth (federate with our backend) ----------------------------------------
export async function firebaseSignInWithCustomToken(token) {
  if (!state.ready) return null;
  return signInWithCustomToken(state.auth, token);
}

export async function firebaseSignOut() {
  if (!state.ready) return;
  await fbSignOut(state.auth);
}

export function onFirebaseAuthChange(cb) {
  if (!state.ready) return () => {};
  return onAuthStateChanged(state.auth, cb);
}
